import {
  ActivitiesControllerApi,
  ActivityAttachmentControllerApi,
  CodelistControllerApi,
  QueryManagerControllerApi,
  type ActivityAttachmentView,
  type ActivityView,
  type QueryQueueItem,
} from '@/lib/api/generated/casedb';
import { catalogApiClient } from '@/features/catalog_case_review/shared/services/catalogApiClient';
import { getOrCreateSessionId } from '@/features/catalog_case_review/shared/services/caseLockService';
import { toCatalogApiError } from '@/features/catalog_case_review/shared/utils/catalogErrors';
import {
  CLOSE_REASON_ACTIVE_STATUS,
  CLOSE_REASON_CODELIST_TABLE,
  CLOSE_REASON_ROW_KEYS,
} from '@/features/catalog_case_review/queries/constants/queryCloseReasonCodelist';
import { QUERY_QUESTION_STATUSES } from '@/features/catalog_case_review/queries/constants/queryQuestionStatuses';
import {
  Activity,
  ActivityAttachment,
  ActivityCloseReason,
  ActivityQuery,
  CloseActivityRequest,
  QueryManagerQueueFilters,
  QueryManagerSummary,
  QueryProgress,
  QueueActivity,
  RaiseQueryRequest,
  RaiseQueryResult,
} from '@/types/activity';

/**
 * The Query Manager and the case Activities tab, over the generated case-db client.
 *
 * The legacy Python service served these routes before #262. The mappers below turn the generated
 * types (every field optional) into the screen types, and fail loudly when a required field is absent.
 */

const OPEN_QUESTION = QUERY_QUESTION_STATUSES.open;

function required<T>(value: T | null | undefined, field: string): T {
  if (value === undefined || value === null) {
    throw new Error(`The activity response has no ${field}.`);
  }
  return value;
}

function unwrap<T>(data: T | undefined, what: string): T {
  if (data === undefined) throw new Error(`The ${what} response was empty.`);
  return data;
}

function toQuery(view: ActivityView['query_extension']): ActivityQuery | null {
  if (!view) return null;
  return {
    id: required(view.id, 'query_extension.id'),
    activity_id: required(view.activity_id, 'query_extension.activity_id'),
    attempt_number: view.attempt_number ?? null,
    close_reason_id: view.close_reason_id ?? null,
    close_comment: view.close_comment ?? null,
    response_at: view.response_at ?? null,
    auto_followup_enabled: required(view.auto_followup_enabled, 'query_extension.auto_followup_enabled'),
    auto_closed: required(view.auto_closed, 'query_extension.auto_closed'),
    auto_close_on: view.auto_close_on ?? null,
  };
}

function toProgress(view: ActivityView['progress']): QueryProgress | null {
  if (!view) return null;
  return {
    planned_attempts: view.planned_attempts ?? null,
    next_attempt_number: view.next_attempt_number ?? null,
    next_attempt_due_on: view.next_attempt_due_on ?? null,
    reminder_draft: required(view.reminder_draft, 'progress.reminder_draft'),
    response_received: required(view.response_received, 'progress.response_received'),
  };
}

function toActivity(view: ActivityView | QueryQueueItem): Activity {
  return {
    id: required(view.id, 'id'),
    case_id: required(view.case_id, 'case_id'),
    parent_activity_id: view.parent_activity_id ?? null,
    activity_type_id: required(view.activity_type_id, 'activity_type_id'),
    title: required(view.title, 'title'),
    description: required(view.description, 'description'),
    status: required(view.status, 'status'),
    assigned_to: view.assigned_to ?? null,
    created_by: required(view.created_by, 'created_by'),
    opened_at: required(view.opened_at, 'opened_at'),
    due_date: view.due_date ?? null,
    closed_at: view.closed_at ?? null,
    notification_badge: required(view.notification_badge, 'notification_badge'),
    query_extension: toQuery(view.query_extension),
    progress: toProgress(view.progress),
    created_at: required(view.created_at, 'created_at'),
    updated_at: required(view.updated_at, 'updated_at'),
  };
}

function toAttachment(view: ActivityAttachmentView): ActivityAttachment {
  return {
    id: required(view.id, 'attachment id'),
    activity_id: required(view.activity_id, 'attachment activity_id'),
    file_name: required(view.file_name, 'attachment file_name'),
    storage_path: required(view.storage_path, 'attachment storage_path'),
    file_type: view.file_type ?? null,
    uploaded_by: required(view.uploaded_by, 'attachment uploaded_by'),
    uploaded_at: required(view.uploaded_at, 'attachment uploaded_at'),
  };
}

function toQueueActivity(item: QueryQueueItem): QueueActivity {
  return {
    ...toActivity(item),
    assigned_to_name: item.assigned_to_name ?? null,
    is_serious: required(item.is_serious, 'is_serious'),
    case_processor_name: item.case_processor_name ?? null,
    open_query_count: required(item.open_query_count, 'open_query_count'),
    open_question_count: (item.questions ?? []).filter((question) => question.status === OPEN_QUESTION).length,
    attachments: required(item.attachments, 'attachments').map(toAttachment),
  };
}

export async function getQueryManagerQueue(filters: QueryManagerQueueFilters = {}): Promise<QueueActivity[]> {
  const client = await catalogApiClient(QueryManagerControllerApi);
  const response = await client.listQueryQueue(
    filters.status,
    filters.assigned_to,
    filters.attempt_number,
    filters.due_date_from,
    filters.due_date_to,
    filters.reminder_ready,
    filters.auto_closed,
    filters.offset,
    filters.limit,
  );
  return unwrap(response.data.data, 'query queue').map(toQueueActivity);
}

export async function getActivitiesSummary(): Promise<QueryManagerSummary> {
  const client = await catalogApiClient(QueryManagerControllerApi);
  const summary = unwrap((await client.queryManagerSummary()).data.data, 'query summary');
  return {
    overdue_count: required(summary.overdue_count, 'overdue_count'),
    due_soon_count: required(summary.due_soon_count, 'due_soon_count'),
    open_count: required(summary.open_count, 'open_count'),
    total_count: required(summary.total_count, 'total_count'),
  };
}

export async function getCaseActivities(caseId: number): Promise<Activity[]> {
  const client = await catalogApiClient(ActivitiesControllerApi);
  return unwrap((await client.listActivities(caseId)).data.data, 'case activities').map(toActivity);
}

/**
 * Sends a write and turns a refusal into an error that carries the server's message, so a drawer
 * can show why (for example "Hold the case edit lock" or "This query is closed").
 */
async function send<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request();
  } catch (failure) {
    throw toCatalogApiError(failure);
  }
}

/** The server owns the title, the assignee and the due date. */
export async function raiseQuery(caseId: number, payload: RaiseQueryRequest): Promise<RaiseQueryResult> {
  const client = await catalogApiClient(ActivitiesControllerApi);
  const response = await send(() => client.raiseQuery(caseId, payload, getOrCreateSessionId()));
  const result = unwrap(response.data.data, 'raise query');
  return {
    activity: toActivity(required(result.activity, 'activity')),
  };
}

export async function closeActivity(activityId: number, payload: CloseActivityRequest): Promise<Activity> {
  const client = await catalogApiClient(ActivitiesControllerApi);
  const response = await send(() => client.closeActivity(activityId, payload, getOrCreateSessionId()));
  return toActivity(unwrap(response.data.data, 'close query'));
}

export async function uploadAttachment(activityId: number, file: File): Promise<ActivityAttachment> {
  const client = await catalogApiClient(ActivityAttachmentControllerApi);
  const response = await send(() => client.uploadActivityAttachment(activityId, file, getOrCreateSessionId()));
  return toAttachment(unwrap(response.data.data, 'attachment upload'));
}

export async function getAttachments(activityId: number): Promise<ActivityAttachment[]> {
  const client = await catalogApiClient(ActivityAttachmentControllerApi);
  return unwrap((await client.listActivityAttachments(activityId)).data.data, 'attachments').map(toAttachment);
}

export async function clearActivityBadge(activityId: number): Promise<void> {
  const client = await catalogApiClient(ActivitiesControllerApi);
  await client.clearBadge(activityId);
}

/** Only the active reasons: the server refuses a retired reason for a new close. */
export async function getActivityCloseReasons(): Promise<ActivityCloseReason[]> {
  const client = await catalogApiClient(CodelistControllerApi);
  const rows: Record<string, unknown>[] = unwrap(
    (await client.getCodelist(CLOSE_REASON_CODELIST_TABLE)).data.data,
    'close reasons',
  );
  return rows
    .filter((row) => row[CLOSE_REASON_ROW_KEYS.status] === CLOSE_REASON_ACTIVE_STATUS)
    .map((row) => ({
      id: Number(required(row[CLOSE_REASON_ROW_KEYS.id], 'close reason id')),
      codelist_value: String(required(row[CLOSE_REASON_ROW_KEYS.value], 'close reason value')),
      display_status: CLOSE_REASON_ACTIVE_STATUS,
    }));
}
